import Vue from 'vue';
import Vue2TouchEvents from 'vue2-touch-events';

Vue.use(Vue2TouchEvents);

const PROGRESS_CIRC = 169.65;

Vue.component('HomeHero', {
  props: {
    slides: {
      type: Array,
      default: () => [],
    },
    autoplay: Boolean,
    initialSlide: Number, // less than 1 means "random'
    delay: Number, // seconds
  },
  data() {
    return {
      stopped: this.autoplay === false,
      activeSlide:
        this.initialSlide >= 1
          ? Math.min(this.initialSlide, this.slides.length) - 1
          : Math.floor(Math.random() * this.slides.length), // randomize intial slide
      timeOut: Math.max(1, this.delay) * 1000, // convert to milliseconds
      innerWidth: window.innerWidth,
      progress: 0,
      rafId: null,
      startTime: null,
    };
  },
  computed: {
    totalSlides() {
      return this.slides.length - 1;
    },
    isMobile() {
      return this.innerWidth < 896;
    },
    mobilePosition() {
      return this.isMobile && this.activeSlide > 0
        ? 'calc(-' +
            this.activeSlide * 100 +
            'vw + ' +
            this.activeSlide * 40 +
            'px + 15px)'
        : '0';
    },
    tabMinWidth() {
      return this.isMobile ? (this.totalSlides + 1) * 100 + 'vw' : '0';
    },
  },
  methods: {
    pause() {
      if (this.stopped) {
        this.stopped = false;
        this.startRafTimer();
      } else {
        this.stopRafTimer();
      }
    },
    updateSize() {
      this.innerWidth = window.innerWidth;
      if (this.isMobile) {
        this.stopRafTimer();
      }
    },
    isActiveSlide(index) {
      return index === this.activeSlide;
    },
    selectSlide(index) {
      this.activeSlide = index;
      this.stopRafTimer();
    },
    switchSlide(index) {
      this.activeSlide = this.activeSlide < this.totalSlides ? index + 1 : 0;

      if (!this.stopped && this.autoplay !== false) {
        this.$nextTick(() => this.startRafTimer());
      } else {
        this.stopRafTimer();
      }
    },
    startRafTimer() {
      if (this.autoplay === false || this.rafId || this.isMobile) return;
      this.startTime = null;
      this.rafId = requestAnimationFrame((t) => this.rafTick(t));
    },

    stopRafTimer() {
      if (this.rafId) {
        cancelAnimationFrame(this.rafId);
        this.rafId = null;
      }
      this.stopped = true;
      this.progress = 0;
      this.startTime = null;
      this.writeProgressVars(0);
    },
    rafTick(timestamp) {
      if (!this.startTime) {
        this.startTime = timestamp;
      }

      const elapsed = Math.max(0, timestamp - this.startTime);
      const progress = Math.min(elapsed / this.timeOut, 1);
      this.progress = progress;

      const offset = (1 - progress) * PROGRESS_CIRC;

      this.writeProgressVars(progress, offset);

      if (progress >= 1) {
        // complete cycle -> advance slide
        // use nextTick to avoid mutating during RAF callback
        this.rafId = null;
        this.$nextTick(() => this.switchSlide(this.activeSlide));
      } else {
        // continue RAF loop
        this.rafId = requestAnimationFrame((t) => this.rafTick(t));
      }
    },

    writeProgressVars(progress = 0, offsetPx = null) {
      const wrapper = this.$refs && this.$refs.wrapper;
      if (!wrapper) return;
      const strokeDashoffset =
        offsetPx === null ? (1 - progress) * PROGRESS_CIRC : offsetPx;
      wrapper.style.setProperty(
        '--home-hero-offset',
        strokeDashoffset.toFixed(3) + 'px'
      );
    },
    swipeLeftHandler() {
      this.activeSlide =
        this.activeSlide < this.totalSlides ? this.activeSlide + 1 : 0;

      this.stopRafTimer();
    },
    swipeRightHandler() {
      this.activeSlide =
        this.activeSlide > 0 ? this.activeSlide - 1 : this.totalSlides;

      this.stopRafTimer();
    },
  },
  created() {
    if (!this.stopped) {
      this.$nextTick(() => this.startRafTimer());
    }
  },
  mounted() {
    this.updateSize();

    window.addEventListener('resize', () => {
      this.updateSize();
    });

    this.$nextTick(() => {
      // don't apply transitions until initial slide is rendered
      this.$refs.wrapper.classList.add('apply-transitions');
      // initial CSS var value
      this.writeProgressVars(0, PROGRESS_CIRC);
    });
  },
  beforeDestroy() {
    this.stopRafTimer();
  },
});
